import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Rocket, CheckCircle2, AlertTriangle, Clock, Activity, ChevronRight,
  Calendar, Users, TrendingUp, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { PageHero, KpiCard, Section } from "@/components/AnalyticsCommon";

const statusColors = {
  em_andamento: "bg-sky-50 text-sky-700 border-sky-200",
  concluido: "bg-emerald-50 text-emerald-700 border-emerald-200",
  atrasado: "bg-rose-50 text-rose-700 border-rose-200",
  em_risco: "bg-amber-50 text-amber-800 border-amber-200",
};

export default function OnboardingDashboardPage() {
  const { companyParam } = useCompany();
  const [data, setData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      const [d, a] = await Promise.all([
        api.get("/onboarding/dashboard", { params }),
        api.get("/onboarding/assignments", { params }),
      ]);
      setData(d.data);
      setAssignments(a.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyParam]);

  const seedDefaults = async () => {
    try {
      const { data } = await api.post("/onboarding/templates/seed-defaults",
        companyParam ? null : null,
        { params: companyParam ? { company_id: companyParam } : {} });
      toast.success(`${data.created || data.items?.length || 0} trilha(s) padrão criadas.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sortedActive = useMemo(
    () => assignments.filter((a) => a.status !== "concluido")
                     .sort((a, b) => (b.progress_pct || 0) - (a.progress_pct || 0)),
    [assignments],
  );

  if (loading || !data) {
    return <div className="space-y-3 pb-10"><Skeleton className="h-[180px]" /><Skeleton className="h-[300px]" /></div>;
  }

  const k = data.kpis;

  return (
    <div className="space-y-5 pb-10" data-testid="onboarding-dashboard-page">
      <PageHero
        kicker={<><Rocket className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Performance & Analytics</>}
        title="Trilhas de Onboarding"
        description="Acompanhe a integração 30/60/90 dias dos novos colaboradores. Trilhas customizáveis, checklist de etapas com evidências e avaliações periódicas com IA."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/performance/onboarding/trilhas">
              <Button size="sm" variant="outline" className="h-10" data-testid="onb-go-templates">
                Trilhas
              </Button>
            </Link>
            <Link to="/performance/onboarding/atribuir">
              <Button size="sm" className="h-10 bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"
                      data-testid="onb-go-assign">
                <Rocket className="h-3.5 w-3.5 mr-1" /> Iniciar onboarding
              </Button>
            </Link>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="onb-kpis">
        <KpiCard label="Onboardings ativos" value={k.ativos} icon={Activity} accent="emerald" testId="onb-active" />
        <KpiCard label="Concluídos" value={k.concluidos}
                 sub={`${k.media_conclusao_pct}% média atual`} icon={CheckCircle2} accent="ochre" testId="onb-completed" />
        <KpiCard label="Atrasados" value={k.atrasados}
                 sub={`${k.etapas_atrasadas} etapas`} icon={Clock} accent="rose" testId="onb-overdue" />
        <KpiCard label="Em risco" value={k.em_risco} icon={AlertTriangle} accent="ochre" testId="onb-risk" />
      </div>

      {/* Periods */}
      <div className="grid grid-cols-3 gap-3" data-testid="onb-periods">
        <PeriodCard period={30} count={k.no_periodo_30} proximos={k.proximos_30}
                    color="emerald" alerts={data.alertas?.proximos_do_30 || []} />
        <PeriodCard period={60} count={k.no_periodo_60} proximos={k.proximos_60}
                    color="sky" alerts={data.alertas?.proximos_do_60 || []} />
        <PeriodCard period={90} count={k.no_periodo_90} proximos={k.proximos_90}
                    color="ochre" alerts={data.alertas?.proximos_do_90 || []} />
      </div>

      {assignments.length === 0 && (
        <Section kicker="Início rápido" title="Configure trilhas padrão">
          <div className="text-center py-6">
            <Rocket className="h-10 w-10 mx-auto text-[#C5A059] mb-2" />
            <p className="text-[13px] text-muted-foreground mb-3">
              Crie 3 trilhas padrão (Operacional, Administrativo, Liderança) com checklist 30/60/90 prontos.
            </p>
            <Button size="sm" onClick={seedDefaults} className="bg-[#234935] text-white" data-testid="onb-seed-btn">
              Criar trilhas padrão
            </Button>
          </div>
        </Section>
      )}

      {/* Active list */}
      <Section kicker="Em andamento" title="Onboardings ativos" testId="onb-active-list"
               action={<Badge variant="outline" className="text-[10px]">{sortedActive.length} ativo{sortedActive.length !== 1 ? "s" : ""}</Badge>}>
        {sortedActive.length === 0 ? (
          <div className="text-center py-8 text-[13px] text-muted-foreground">
            Nenhum onboarding em andamento.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {sortedActive.map((a) => (
              <Link key={a.id} to={`/performance/onboarding/colaborador/${a.employee_id}`}
                    className="rounded-xl border border-border bg-white p-3.5 hover:shadow-md hover:border-[#234935]/30 transition-all group"
                    data-testid={`onb-card-${a.id}`}>
                <div className="flex items-start gap-3 mb-2">
                  <div className="h-9 w-9 rounded-full bg-[#234935] text-white grid place-items-center text-[12px] font-semibold shrink-0">
                    {(a.employee_name || "?").charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-[13.5px] truncate">{a.employee_name}</div>
                    <div className="text-[10.5px] text-muted-foreground truncate">
                      {a.cargo} · {a.sector_name}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-[#234935] group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="flex items-center justify-between mb-1.5">
                  <Badge variant="outline" className={`text-[9px] capitalize ${statusColors[a.status]}`}>
                    {a.status.replace("_", " ")}
                  </Badge>
                  <span className="text-[10.5px] text-muted-foreground">Dia {a.days_since_start}/90</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-1">
                  <div className={`h-full ${a.status === "atrasado" ? "bg-rose-500" : "bg-[#C5A059]"}`}
                       style={{ width: `${a.progress_pct}%` }} />
                </div>
                <div className="flex items-center justify-between text-[10.5px]">
                  <span className="text-muted-foreground">{a.progress_pct}% concluído</span>
                  <span className="text-muted-foreground font-mono">{a.concluidas}/{a.total_steps}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      {/* By sector + Pendencies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Section kicker="Por setor" title="Conclusão por setor" testId="onb-by-sector">
          {data.by_sector.length === 0 ? (
            <div className="text-[12px] text-muted-foreground">Sem dados.</div>
          ) : (
            <div className="space-y-2">
              {data.by_sector.slice(0, 8).map((s) => (
                <div key={s.sector} className="flex items-center gap-2">
                  <span className="text-[12px] flex-1 truncate">{s.sector}</span>
                  <span className="text-[10.5px] text-muted-foreground font-mono">{s.concluidos}/{s.total}</span>
                  <div className="w-24 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-[#234935]" style={{ width: `${s.taxa_pct}%` }} />
                  </div>
                  <span className="text-[10.5px] font-mono w-10 text-right">{s.taxa_pct}%</span>
                </div>
              ))}
            </div>
          )}
        </Section>
        <Section kicker="Pendências" title="Etapas pendentes por setor" testId="onb-pend">
          {data.pendencias_por_setor.length === 0 ? (
            <div className="text-[12px] text-muted-foreground">Tudo em dia.</div>
          ) : (
            <div className="space-y-1.5">
              {data.pendencias_por_setor.map((p) => (
                <div key={p.sector} className="flex items-center justify-between text-[12px]">
                  <span className="truncate">{p.sector}</span>
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                    {p.pendentes}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function PeriodCard({ period, count, proximos, color, alerts }) {
  const cls = {
    emerald: { ring: "ring-emerald-200", bg: "from-emerald-50", text: "text-emerald-700" },
    sky: { ring: "ring-sky-200", bg: "from-sky-50", text: "text-sky-700" },
    ochre: { ring: "ring-amber-200", bg: "from-amber-50", text: "text-amber-700" },
  }[color];
  return (
    <div className={`rounded-xl border-2 border-border bg-gradient-to-br ${cls.bg} to-white p-4 ring-1 ${cls.ring}`}
         data-testid={`onb-period-${period}`}>
      <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${cls.text} mb-1`}>
        Primeiros {period} dias
      </div>
      <div className="flex items-end justify-between mb-2">
        <div className="font-display text-[28px] font-bold tabular-nums">{count}</div>
        <Calendar className="h-5 w-5 text-muted-foreground/50" />
      </div>
      {proximos > 0 && (
        <div className="text-[10.5px] flex items-center gap-1 text-amber-700 mb-1">
          <AlertCircle className="h-3 w-3" /> {proximos} próximo{proximos !== 1 ? "s" : ""} do prazo
        </div>
      )}
      {alerts.length > 0 && (
        <ul className="text-[10.5px] text-slate-700 space-y-0.5 mt-1">
          {alerts.slice(0, 3).map((al, i) => (
            <li key={i} className="truncate">› {al.nome} ({al.dias}d)</li>
          ))}
        </ul>
      )}
    </div>
  );
}
